import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

import { Alert, Badge, Button, InlineLoader } from '../../components/ui'
import { keys } from '../../api/keys'
import { fetchProperties } from '../../api/objects'
import type { PropertySummary } from '../../api/objects'
import { useSession } from '../../auth/session'
import { t } from '../../i18n'
import { listingStatuses, mediaURL } from '../../api/objects-write'
import { NewPropertyForm } from './NewPropertyForm'

const PAGE = 50

/**
 * Every property we hold, newest work first.
 *
 * The counts on a row are read from the server and shown only when it sent them.
 * A row that quietly displayed "0 listings" because the list query does not count
 * them would send somebody looking for an import that never failed.
 */
export function PropertyList({ selected }: { readonly selected: string | null }) {
  const [limit, setLimit] = useState(PAGE)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState('')
  const { can } = useSession()

  // Sent to the server, never applied to the page that came back: the list is
  // paged, and filtering forty rows out of fifty locally would answer a
  // different question than the one asked.
  const filter = { limit, query, status }

  const properties = useQuery({
    queryKey: keys.properties(filter),
    queryFn: ({ signal }) => fetchProperties(filter, signal),
    placeholderData: keepPreviousData,
  })

  return (
    <>
      {properties.isError ? (
        <div className="inbox__message">
          <Alert tone="error">{t('objects.failed')}</Alert>
          <Button
            variant="secondary"
            onClick={() => {
              void properties.refetch()
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {properties.isPending ? (
        <div className="inbox__message">
          <InlineLoader />
        </div>
      ) : null}

      {can('listings.write') ? (
        <div className="thread-list__actions">
          <NewPropertyForm />
        </div>
      ) : null}

      <div className="thread-list__filters">
        <input
          className="field__input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('objects.search')}
          aria-label={t('objects.search')}
        />
        <select
          className="field__input"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
          aria-label={t('objects.filterStatus')}
        >
          <option value="">{t('objects.filterAll')}</option>
          {listingStatuses.map((value) => (
            <option key={value} value={value}>
              {t(`status.${value}`)}
            </option>
          ))}
        </select>
      </div>

      {properties.data === undefined ? null : (
        <>
          <ol className="thread-list">
            {properties.data.properties.map((property) => (
              <PropertyRow key={property.id} property={property} selected={property.id === selected} />
            ))}
          </ol>

          {properties.data.properties.length === 0 ? (
            <p className="inbox__message inbox__message--quiet">
              {/*
                An empty search and an empty inventory are different facts, and
                only one of them means something is wrong. "Объектов пока нет"
                after a typo reads as a catalogue that has been wiped.
              */}
              {query.trim() === '' && status === ''
                ? t('objects.empty')
                : t('objects.nothingFound')}
            </p>
          ) : null}

          <div className="inbox__footer">
            <span className="inbox__counter">
              {properties.data.more
                ? t('objects.counterMore', { shown: properties.data.properties.length })
                : t('objects.counterAll', { shown: properties.data.properties.length })}
            </span>
            {properties.data.more ? (
              <Button variant="ghost" onClick={() => setLimit((current) => current + PAGE)}>
                {t('common.showMore')}
              </Button>
            ) : null}
          </div>
        </>
      )}
    </>
  )
}

function PropertyRow({
  property,
  selected,
}: {
  readonly property: PropertySummary
  readonly selected: boolean
}) {
  const address = property.addressExact === '' ? property.addressNormalized : property.addressExact
  const classes = ['thread-row']
  if (selected) classes.push('thread-row--selected')

  return (
    <li>
      <NavLink to={`/objects/${property.id}`} className={classes.join(' ')}>
        {/* A photograph is the fastest way to know which of your own flats
            this is. Absent for most of them at first, and a row without one
            is simply a row. */}
        {property.coverMediaId === undefined ? null : (
          <img
            className="thread-row__cover"
            src={mediaURL(property.coverMediaId)}
            alt=""
            loading="lazy"
          />
        )}

        <div className="thread-row__top">
          <span className="thread-row__name">{address === '' ? t('objects.addressUnknown') : address}</span>
        </div>

        <div className="thread-row__preview">
          {[property.city, property.area, property.municipality].filter((part) => part !== '').join(' · ')}
        </div>

        <div className="thread-row__badges">
          {property.unitCount === undefined ? null : (
            <Badge tone="muted">{t('objects.unitCount', { count: property.unitCount })}</Badge>
          )}
          {property.listingCount === undefined ? null : (
            <Badge tone="muted">{t('objects.listingCount', { count: property.listingCount })}</Badge>
          )}
          {/*
            The only count worth colouring: how many of these could be put in
            front of a client today. Everything else on the row is inventory; this
            is the answer to "have we got anything for them".
          */}
          {property.offerableCount === undefined ? null : (
            <Badge tone={property.offerableCount > 0 ? 'accent' : 'warning'}>
              {t('objects.offerableCount', { count: property.offerableCount })}
            </Badge>
          )}
        </div>
      </NavLink>
    </li>
  )
}
